import { skipToken } from '@reduxjs/toolkit/query';
import { buttonStyle } from 'components/buttons/buttonStyle';
import EmptyState from 'components/EmptyState';
import { EditOutlined, RocketLaunchOutlined, TimerOutlined } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { useCurrentEmployee } from 'pages/employees/useCurrentEmployee';
import { ReactNode, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetFinalizedHoursQuery } from 'store/api/dashboard';
import { DashboardForm } from 'types/dashboard';
import { cn } from 'utils/cn';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import PeriodPicker from '../finalized-hours/PeriodPicker';
import { PANEL_INSET } from '../finalized-hours/util';
import EditTargetsModal from './EditTargetsModal';
import {
    computeProgress,
    DeltaTone,
    formatHours,
    formatSignedHours,
    getNominalRange,
    NO_VALUE,
    usableTargets,
} from './progressCalculation';
import { useProgressMetric } from './progressMetric';

const TONE_CLASS: Record<DeltaTone, string> = {
    neutral: 'text-fg',
    positive: 'text-fg-success',
    negative: 'text-destructive',
};

const LABEL_CLASS = 'text-fg-secondary text-xs leading-5';
const FOOTER_CLASS = 'flex h-[90px] shrink-0 items-center gap-2.5';
/** The grey band. No vertical padding: `items-center` alone centres every state in its fixed height. */
const BAND_CLASS = cn(FOOTER_CLASS, 'bg-muted border-divider rounded-[10px] border-t px-4');

const share = (value: number) => `${value * 100}%`;

/**
 * The bar's 13px plus 8px: the target tick rises above the bar, the expected tick drops below it, and where the two
 * meet they form one line. It moves by its bar-wide wrapper's `translate`, which runs no layout, rather than `left`.
 */
const Tick = ({ at, className }: { at: number; className: string }) => (
    <div
        aria-hidden
        className="absolute inset-x-0 top-0 motion-safe:transition-[translate] motion-safe:duration-300"
        style={{ translate: share(at) }}
    >
        <div className={cn('bg-fg absolute left-0 h-[21px] w-[1.4px] -translate-x-1/2 rounded-[1px]', className)} />
    </div>
);

/**
 * Shifted back by the share it sits at: left-aligned near the start, centred mid-bar, right-aligned near the end, so
 * it stays in the card at any width and ends on a tick at the end of the bar. It moves with a bar-wide wrapper, as a
 * tick does, which lets the pointer through to the row under it.
 */
const TickLabel = ({ at, className, children }: { at: number; className: string; children: ReactNode }) => (
    <div
        className="pointer-events-none absolute inset-0 motion-safe:transition-[translate] motion-safe:duration-300"
        style={{ translate: share(at) }}
    >
        <div
            className={cn(
                'text-fg-secondary pointer-events-auto absolute left-0 flex items-center gap-1 text-xs tracking-[0.03em] whitespace-nowrap motion-safe:transition-[translate] motion-safe:duration-300',
                className
            )}
            style={{ translate: `-${share(at)}` }}
        >
            {children}
        </div>
    </div>
);

/**
 * A `separated` figure hangs its "|" in the 20px gap before it. The header row clips horizontally, so when the
 * figure wraps to the start of a line its separator falls outside and is cut: a "|" never ends or starts a line.
 */
const Figure = ({ labelId, value, separated }: { labelId: string; value: string; separated?: boolean }) => (
    <span className="relative flex items-center gap-2">
        {separated ? (
            <span aria-hidden className={cn(LABEL_CLASS, 'absolute -left-3')}>
                |
            </span>
        ) : null}
        <span className={LABEL_CLASS}>
            <FormattedMessage id={labelId} />
        </span>
        <span className="text-fg text-body1 font-medium">{value}</span>
    </span>
);

const LoadError = ({ onRetry }: { onRetry: () => void }) => (
    <div className="flex h-full items-center justify-center">
        <EmptyState size="panel" variant="error" onRetry={onRetry} surface="dashboard-progress-tracking" />
    </div>
);

const ProgressSkeleton = () => (
    <div className="flex h-full flex-col gap-4 p-4" aria-busy>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="mt-auto h-[13px] w-full rounded-full" />
        <Skeleton className="h-[90px] w-full" />
    </div>
);

/** The signed-in employee's hours for the dashboard period, set against their own targets. */
const ProgressTracking = () => {
    const { control } = useFormContext<DashboardForm>();
    const [dateFrom, dateTo, selectedTimeRange] = useWatch({
        control,
        name: ['dateFrom', 'dateTo', 'selectedTimeRange'],
    });
    const [today] = useState(() => new Date());
    const { displayDecimalScale } = useTimecardPrecision();
    const { openModal, closeModal } = useModal();
    const {
        data: employee,
        isLoading: isLoadingEmployee,
        isError: isEmployeeError,
        refetch: refetchEmployee,
    } = useCurrentEmployee();
    const metric = useProgressMetric();
    // The Finalised hours tab passes the same arguments, so the two tabs share one cached response.
    const {
        data: hours,
        isFetching,
        isError,
        refetch,
    } = useGetFinalizedHoursQuery(!(dateFrom || dateTo) ? skipToken : { dateFrom, dateTo });

    const annualTargetHours = employee?.annualTargetHours;
    const plannedWorkdaysOff = employee?.plannedWorkdaysOff;
    const openEditTargets = () =>
        openModal(
            <EditTargetsModal
                onClose={closeModal}
                annualTargetHours={annualTargetHours}
                plannedWorkdaysOff={plannedWorkdaysOff}
            />,
            closeModal
        );

    if (isLoadingEmployee) {
        return <ProgressSkeleton />;
    }
    // Without the employee the targets are unknown, not unset: offering to set them would overwrite them.
    if (isEmployeeError && !employee) {
        return <LoadError onRetry={refetchEmployee} />;
    }
    const targets = usableTargets(annualTargetHours, plannedWorkdaysOff, today.getFullYear());
    if (!targets) {
        return (
            <div className="flex h-full items-center justify-center">
                <EmptyState
                    size="panel"
                    icon={RocketLaunchOutlined}
                    titleId="dashboard.progress-tracking.placeholder"
                    surface="dashboard-progress-tracking"
                    action={
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                            onClick={openEditTargets}
                        >
                            <FormattedMessage id="dashboard.progress-tracking.set-target" />
                        </Button>
                    }
                />
            </div>
        );
    }
    if (isError && !hours) {
        return <LoadError onRetry={refetch} />;
    }
    const range = selectedTimeRange ? getNominalRange(selectedTimeRange.value, today, dateFrom, dateTo) : null;
    if (!selectedTimeRange || !range || !hours) {
        return <ProgressSkeleton />;
    }

    const actualHours = hours[metric.field];
    const progress = computeProgress({
        option: selectedTimeRange.value,
        range,
        today,
        ...targets,
        actualHours,
    });
    const { bar, caption, message, projectedTotal } = progress;

    return (
        <div className="flex h-full flex-col gap-[18px]">
            <div className={cn('flex min-h-0 flex-1 flex-col', PANEL_INSET)}>
                <div className="flex items-start justify-between gap-2">
                    {/* The 4px padding, cancelled by the margin, keeps the button's focus ring inside the clip. */}
                    <div className="-mx-1 flex min-w-0 flex-wrap items-center gap-x-5 gap-y-1 overflow-x-clip px-1">
                        <Figure
                            labelId="dashboard.progress-tracking.annual-target"
                            value={formatHours(targets.annualTargetHours, 0)}
                        />
                        {progress.isFullYear || progress.isSingleDay ? null : (
                            <Figure
                                separated
                                labelId="dashboard.progress-tracking.period-target"
                                value={formatHours(progress.periodTarget, displayDecimalScale)}
                            />
                        )}
                        {progress.isFullYear ? null : (
                            <Figure
                                separated
                                labelId="dashboard.progress-tracking.daily-target"
                                value={formatHours(progress.dailyTarget, displayDecimalScale)}
                            />
                        )}
                        <Button variant="outline" size="xs" className="text-fg" onClick={openEditTargets}>
                            <EditOutlined className="size-3.5" />
                            <FormattedMessage id="dashboard.progress-tracking.edit-targets" />
                        </Button>
                    </div>
                    <div className="shrink-0">
                        <PeriodPicker />
                    </div>
                </div>
                {/* Anchored to the bottom so a wrapping header row cannot push the bar into the footer; the
                    bottom margin clears the expected label, which hangs below the bar out of the flow. Clipped on x:
                    a tick's or a label's wrapper reaches up to a bar's width past its end, which would scroll a tab
                    sideways. The 4px padding, cancelled by the margin, keeps an end tick and a focus ring inside. */}
                <div
                    className={cn(
                        '-mx-1 mt-auto mb-[39px] flex flex-col gap-0.5 overflow-x-clip px-1 transition-opacity duration-150',
                        isFetching && 'opacity-35'
                    )}
                >
                    <div className="relative flex h-[22px] items-center gap-2">
                        <span className={LABEL_CLASS}>
                            <FormattedMessage id={metric.labelId} />
                        </span>
                        <InfoTooltip text={metric.tooltipId} className="-ml-1 h-4" />
                        <span className="text-fg text-body1 tracking-[0.03em]">
                            {formatHours(actualHours, displayDecimalScale)}
                            {progress.percent === null ? null : ` (${progress.percent}%)`}
                        </span>
                        {bar ? (
                            <TickLabel at={bar.targetTick} className="inset-y-0">
                                <FormattedMessage
                                    id="dashboard.progress-tracking.target"
                                    values={{ hours: formatHours(progress.periodTarget, displayDecimalScale) }}
                                />
                            </TickLabel>
                        ) : null}
                    </div>
                    {bar ? (
                        <div className="relative h-[22px]">
                            <div className="bg-muted absolute inset-x-0 top-[4.5px] h-[13px] overflow-hidden rounded-full">
                                {/* Full width and slid back by what is left to fill, inside the track's clip. */}
                                <div
                                    className="bg-secondary h-full w-full rounded-full motion-safe:transition-[translate] motion-safe:duration-300"
                                    style={{ translate: `-${share(1 - bar.fill)}` }}
                                />
                            </div>
                            <Tick at={bar.targetTick} className="top-[-3.5px]" />
                            {bar.expectedTick === null ? null : (
                                <>
                                    <Tick at={bar.expectedTick} className="top-[4.5px]" />
                                    <TickLabel at={bar.expectedTick} className="top-[27px]">
                                        <FormattedMessage
                                            id="dashboard.progress-tracking.expected"
                                            values={{
                                                hours: formatHours(progress.expectedHours, displayDecimalScale),
                                            }}
                                        />
                                        <InfoTooltip
                                            text={
                                                progress.isFullYear
                                                    ? 'dashboard.progress-tracking.expected-tooltip-full-year'
                                                    : 'dashboard.progress-tracking.expected-tooltip'
                                            }
                                            values={{
                                                annualTarget: formatHours(targets.annualTargetHours, 0),
                                                dailyTarget: formatHours(progress.dailyTarget, displayDecimalScale),
                                                elapsed: progress.elapsed,
                                                weekdays: progress.weekdays,
                                            }}
                                            className="h-4"
                                        />
                                    </TickLabel>
                                </>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
            {message ? (
                <div className={cn(BAND_CLASS, 'justify-center gap-1')}>
                    <p className="text-fg-secondary m-0 text-xs leading-6 tracking-[0.03em]">
                        <FormattedMessage id={`dashboard.progress-tracking.message.${message}`} />
                    </p>
                    {message === 'future' ? null : <TimerOutlined className="text-fg-secondary size-4" />}
                </div>
            ) : caption ? (
                <div className={cn(BAND_CLASS, 'transition-opacity duration-150', isFetching && 'opacity-35')}>
                    <p className="m-0 flex-1 text-base leading-6 font-semibold tracking-[0.03em]">
                        <span className={TONE_CLASS[progress.tone]}>
                            {formatSignedHours(progress.delta, displayDecimalScale)}
                        </span>{' '}
                        <span className="text-fg">
                            <FormattedMessage id={`dashboard.progress-tracking.caption.${caption}`} />
                        </span>
                    </p>
                    {/* Laid out even when empty, so the delta keeps the same width with and without a projection. */}
                    <div
                        className={cn('flex flex-col items-end gap-0.5', projectedTotal === null && 'invisible')}
                        aria-hidden={projectedTotal === null}
                    >
                        <span className={LABEL_CLASS}>
                            <FormattedMessage id="dashboard.progress-tracking.projected" />
                        </span>
                        <span className="text-fg text-base leading-6 font-semibold tracking-[0.03em]">
                            {projectedTotal === null ? NO_VALUE : formatHours(projectedTotal, displayDecimalScale)}
                        </span>
                    </div>
                </div>
            ) : (
                <div aria-hidden className={FOOTER_CLASS} />
            )}
        </div>
    );
};

export default ProgressTracking;
